import { resolveScenario } from "@/mocks/defineContract"
import { mockRegistry } from "@/mocks/registry"

/**
 * Dev-only mock transport. Answers ONLY the operations listed in
 * VITE_MOCK_OPERATIONS and passes everything else through to the real API, so
 * one unbuilt endpoint can be mocked while the rest of the app stays live.
 *
 * Wired as `createClient({ fetch: mockFetch })` behind `import.meta.env.DEV`,
 * so production builds tree-shake it out entirely and the exchanges array is
 * left untouched.
 *
 * Zero added dependencies: urql already supports a custom fetch
 * (ClientOptions.fetch) and puts `operationName` in the request body
 * (makeFetchBody), which is all the routing information needed. Writing an urql
 * Exchange instead would mean importing wonka stream operators.
 *
 * Selection syntax (frontend/.env.development.local):
 *   VITE_MOCK_OPERATIONS=GetPurchaseOrder:success,PurchaseOrderList:success:empty
 */

/** Scenario keys contain colons (`error:BAD_REQUEST`), so split on the FIRST colon only. */
const parseSelections = (raw: string | undefined): Map<string, string> => {
  const selections = new Map<string, string>()
  if (!raw) return selections

  for (const entry of raw.split(",")) {
    const trimmed = entry.trim()
    if (!trimmed) continue

    const separator = trimmed.indexOf(":")
    if (separator === -1) {
      console.warn(`[mock-api] ignoring "${trimmed}" — expected <Operation>:<scenario>`)
      continue
    }

    const operation = trimmed.slice(0, separator)
    const scenario = trimmed.slice(separator + 1)
    const contract = mockRegistry[operation]

    if (!contract) {
      const known = Object.keys(mockRegistry).join(", ") || "none"
      console.warn(`[mock-api] no contract for "${operation}". Registered: ${known}`)
      continue
    }
    if (!contract.scenarios[scenario]) {
      console.warn(
        `[mock-api] "${operation}" has no scenario "${scenario}". Available: ${Object.keys(contract.scenarios).join(", ")}`
      )
      continue
    }

    selections.set(operation, scenario)
  }

  return selections
}

const selections = parseSelections(import.meta.env.VITE_MOCK_OPERATIONS as string | undefined)

/**
 * True only when at least one operation is actually selected. The urql client
 * uses this to decide whether to install the override at all, so with no
 * contracts (or no VITE_MOCK_OPERATIONS) the native fetch stays in the request
 * path and this module contributes nothing at runtime.
 */
export const hasMockSelections = selections.size > 0

/**
 * Recovers the operation name from an outgoing request. urql POSTs a JSON body
 * containing `operationName`, falling back to GET query params only when
 * `preferGetMethod` is set — handled anyway so a future context change cannot
 * silently disable mocking.
 *
 * FormData bodies (file uploads) return undefined and pass straight through.
 */
const getOperationName = (input: RequestInfo | URL, init?: RequestInit): string | undefined => {
  const body = init?.body

  if (typeof body === "string") {
    try {
      return (JSON.parse(body) as { operationName?: string }).operationName ?? undefined
    } catch {
      return undefined
    }
  }

  if (!body) {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url
    if (!url.includes("?")) return undefined
    return new URLSearchParams(url.slice(url.indexOf("?") + 1)).get("operationName") ?? undefined
  }

  return undefined
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

export const mockFetch: typeof fetch = async (input, init) => {
  const operationName = getOperationName(input, init)
  const scenarioKey = operationName ? selections.get(operationName) : undefined

  if (!operationName || !scenarioKey) {
    return fetch(input, init)
  }

  const { body, networkError, delayMs } = resolveScenario(mockRegistry[operationName], scenarioKey)

  if (delayMs) await sleep(delayMs)

  console.info(`[mock-api] ${operationName} → ${scenarioKey}`)

  // Throwing is how a transport failure reaches urql — it surfaces as
  // CombinedError.networkError, exactly like a dropped connection.
  if (networkError) throw new Error(networkError)

  // The body is already a spec-compliant GraphQL response, so urql builds the
  // CombinedError itself rather than us hand-constructing one.
  return new Response(JSON.stringify(body), {
    status: 200,
    headers: { "content-type": "application/json" },
  })
}

if (selections.size > 0) {
  console.info(
    `[mock-api] mocking ${selections.size} operation(s):`,
    [...selections].map(([operation, scenario]) => `${operation}:${scenario}`).join(", ")
  )
}
